import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { getRoute } from "../actions/sessions";
import { isRenderableContinuation } from "../lib/utils/post_approval_plaid";
import {
  capturePostApprovalPlaidEvent,
  POST_APPROVAL_PLAID_EVENTS,
} from "../lib/utils/post_approval_plaid_events";
import { offerView } from "../lib/utils/post_approval_plaid_offer";
import { baselineCreditLimit } from "../lib/utils/post_approval_plaid_skip";
import usePostApprovalPlaidContext from "./usePostApprovalPlaidContext";

const read = (value, key) => value?.get?.(key) ?? value?.[key];
const MAX_OFFER_EVENT_CONTEXTS = 20;
const MAX_OFFER_EVENT_ATTEMPTS = 20;
const emittedOfferAttemptIds = new Map();

const toSafeCents = (creditLimit) => {
  if (!Number.isFinite(creditLimit)) return null;

  const cents = Math.round(creditLimit * 100);
  return Number.isSafeInteger(cents) ? cents : null;
};

const offerEventWasEmitted = ({
  applicationId,
  attemptId,
  publicKey,
  sessionId,
}) => {
  const contextKey = JSON.stringify([publicKey, sessionId, applicationId]);
  let attempts = emittedOfferAttemptIds.get(contextKey);
  if (attempts?.has(attemptId)) return true;

  if (!attempts) {
    if (emittedOfferAttemptIds.size === MAX_OFFER_EVENT_CONTEXTS) {
      emittedOfferAttemptIds.delete(emittedOfferAttemptIds.keys().next().value);
    }
    attempts = new Map();
  } else {
    emittedOfferAttemptIds.delete(contextKey);
  }

  if (attempts.size === MAX_OFFER_EVENT_ATTEMPTS) {
    attempts.delete(attempts.keys().next().value);
  }
  attempts.set(attemptId, true);
  emittedOfferAttemptIds.set(contextKey, attempts);
  return false;
};

const usePostApprovalPlaidOffer = () => {
  const dispatch = useDispatch();
  const plaid = usePostApprovalPlaidContext();
  const store = useStore();
  const session = useSelector((state) => state.sessions.get('session'));
  const publicKey = useSelector((state) => state.sessions.get('public_key'));
  const company = useSelector((state) =>
    state.entities.getIn(['company', session.get('company_id')])
  );
  const mounted = useRef(false);
  const applicationId = session.get('application_id');
  const sessionId = session.get('id');
  const currentOfferView = offerView({
    attempt: plaid.attempt,
    error: plaid.error,
    hasAttempt: plaid.hasAttempt,
    loading: plaid.loading,
    preAttemptCanContinue: plaid.preAttemptCanContinue,
  });

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (currentOfferView !== 'offer') return;

    const attemptStatus = plaid.hasAttempt
      ? read(plaid.attempt, 'status')
      : null;
    const attemptId = read(plaid.attempt, 'attempt_id') ?? 'pre_attempt';
    if (
      offerEventWasEmitted({
        applicationId,
        attemptId,
        publicKey,
        sessionId,
      })
    )
      return;
    const attempt =
      read(plaid.attempt, 'attempt_id') == null
        ? { attempt_id: attemptId, status: attemptStatus }
        : plaid.attempt;
    capturePostApprovalPlaidEvent(POST_APPROVAL_PLAID_EVENTS.OFFER_VIEWED, {
      session,
      applicationId,
      attempt,
      hasAttempt: Boolean(plaid.hasAttempt),
      attemptStatus,
      baselineCreditLimitCents: toSafeCents(
        baselineCreditLimit({ attempt: plaid.attempt, company })
      ),
      underwritingEnhanced: Boolean(
        read(plaid.attempt, 'company_underwriting_enhanced')
      ),
      accountReady: Boolean(read(plaid.attempt, 'account_ready')),
    });
  }, [
    applicationId,
    company,
    currentOfferView,
    plaid.attempt,
    plaid.hasAttempt,
    publicKey,
    session,
    sessionId,
  ]);

  const continueToNextRoute = useCallback(
    (history) => {
      const matchesContext = () => {
        const sessions = store.getState().sessions;
        return mounted.current &&
          sessions.get('public_key') === publicKey &&
          sessions.getIn(['session', 'id']) === session.get('id') &&
          sessions.getIn(['session', 'application_id']) === session.get('application_id');
      };
      if (!matchesContext() || plaid.phase === "entity_refresh_error") return undefined;

      const currentError =
        (plaid.error?.get
          ? plaid.error.get("http_status")
          : plaid.error?.http_status) === 422;
      const attemptContinuation = currentError
        ? plaid.error
        : plaid.hasAttempt
        ? plaid.attempt
        : plaid.error || {
            can_continue: plaid.preAttemptCanContinue,
            next_route_path: plaid.preAttemptNextRoutePath,
          };
      const canContinue = attemptContinuation?.get
        ? attemptContinuation.get("can_continue")
        : attemptContinuation?.can_continue;
      const nextRoutePath = attemptContinuation?.get
        ? attemptContinuation.get("next_route_path")
        : attemptContinuation?.next_route_path;

      if (canContinue && isRenderableContinuation(nextRoutePath)) {
        return history.push(nextRoutePath);
      }

      return dispatch(getRoute({ canApplyResponse: matchesContext }));
    },
    [
      dispatch,
      store,
      session,
      publicKey,
      plaid.attempt,
      plaid.error,
      plaid.hasAttempt,
      plaid.phase,
      plaid.preAttemptCanContinue,
      plaid.preAttemptNextRoutePath,
    ]
  );

  return {
    ...plaid,
    continueToNextRoute,
    offerView: currentOfferView,
  };
};

export default usePostApprovalPlaidOffer;
